import { useEffect, useState } from "react"
import { Accordion, CheckboxButton, CheckboxButtonGroup, DatePickerCustom, FormError, InputLista, InputNumber, Modal, FormLabel } from "../../common"
import { useForm, useFormErrors, useManagedContext } from "../../hooks"
import { onRequestError, onRequestNoSuccess } from "../../../utils/requests"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../../config/apis"
import { isValidNumber, isNull } from "../../../utils/validator"
import { ServerRequest } from "../../../utils/apiweb"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"
import {TIPS} from "../../../consts/tips";
import RubroAlicuotasGrid from "../RubroAlicuotasGrid"

const entityInit = {
    id: -1,
    codigo: '',
    nombre: '',
    orden: '',
    agrupamiento: '',
    fechaBaja: null,
    facturable: false,
    generico: false,
    categoria: '',
    importeMinimo: 0,
    alicuota: 0,
    regimenGeneral: false,
    idTipoMinimoAplicable: 0,
}

const RubroModal = (props) => {
    const { setIsLoading } = useManagedContext()

    const [entity, setEntity] = useState(entityInit)
    const [internalMode, setInternalMode] = useState(props.editMode)
    const [alicuotas, setAlicuotas] = useState([])

    const [formValues, formHandle, _, formSet] = useForm({
        codigo: '',
        nombre: '',
        orden: '',
        agrupamiento: '',
        fechaBaja: null,
        facturable: false,
        generico: false,
        categoria: '',
        importeMinimo: 0,
        alicuota: 0,
        regimenGeneral: false,
        idTipoMinimoAplicable: 0,
    })

    const disabled = internalMode === 'view'

    useEffect(() => {
        const {
            codigo,
            nombre,
            orden,
            agrupamiento,
            fechaBaja,
            facturable,
            generico,
            categoria,
            importeMinimo,
            alicuota,
            regimenGeneral,
            idTipoMinimoAplicable,
        } = props.rubro

        setEntity({ ...entityInit, ...props.rubro   })
        formSet({
            ...entityInit,
            codigo,
            nombre,
            orden,
            agrupamiento,
            fechaBaja,
            facturable,
            generico,
            categoria,
            importeMinimo,
            alicuota,
            regimenGeneral,
            idTipoMinimoAplicable,
        })
    }, [props.rubro])

    useEffect(() => {
        if (internalMode !== 'add' && entity.id > 0) {
            ServerRequest(
                REQUEST_METHOD.GET,
                null,
                true,
                APIS.URLS.RUBRO,
                `/dto/${entity.id}`,
                null,
                res => res.json().then(data => {
                    const parsed = (data.alicuotas || []).map(a => ({
                        ...a,
                        fechaDesde: a.fechaDesde ? new Date(a.fechaDesde) : null,
                        fechaHasta: a.fechaHasta ? new Date(a.fechaHasta) : null,
                    }))
                    setAlicuotas(parsed)
                }),
                res => onRequestNoSuccess(res),
                error => onRequestError(error),
            )
        }
    }, [entity.id, internalMode])

    const [datosOpen, setDatosOpen] = useState(true)

    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors)

    function getErrors() {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED
        if (formValues.nombre.length < 1) errors.nombre = VALIDATION_MESSAGE.REQUIRED
        if (formValues.agrupamiento.length < 1) errors.agrupamiento = VALIDATION_MESSAGE.REQUIRED
        if (formValues.categoria.length < 1) errors.categoria = VALIDATION_MESSAGE.REQUIRED
        if (isEmptyValue(formValues.orden)) errors.orden = VALIDATION_MESSAGE.REQUIRED
        if (!isValidNumber(formValues.idTipoMinimoAplicable, true)) errors.idTipoMinimoAplicable = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    function getFormatErrors() {
        const errors = {}
        if (disabled) return errors

        if (!isEmptyValue(formValues.orden) && !isValidNumber(formValues.orden, true)) errors.orden = 'Ingresá un número mayor a cero'

        return errors
    }

    function isEmptyValue(valor) {
        return isNull(valor) || valor.toString().length === 0
    }

    const onConfirm = () => {
        if (Object.keys({...getErrors(), ...getFormatErrors()}).length > 0) setDatosOpen(true)

        if (!validate()) return

        setIsLoading(true)

        const payload = {
            rubro: {...entity, ...formValues, id: entity.id, orden: parseInt(formValues.orden) ?? 0},
            alicuotas: alicuotas,
            archivos: [],
            observaciones: [],
            etiquetas: [],
        }

        ServerRequest(
            internalMode === 'add' ? REQUEST_METHOD.POST : REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.RUBRO,
            internalMode === 'modify' ? `/${entity.id}` : '',
            payload,
            res => res.json().then((data) => {
                setIsLoading(false)
                if (internalMode === 'add') {
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Rubro creado correctamente')
                    const newId = data?.rubro?.id || data?.id
                    setEntity(prev => ({ ...prev, id: newId }))
                    setInternalMode('modify')
                } else {
                    props.onConfirm()
                }
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            error => {
                onRequestError(error)
                setIsLoading(false)
            },
        )
    }

    return (
        <Modal
            onEscape={() => {
                if (internalMode !== props.editMode) {
                    props.onConfirm()
                } else {
                    props.onCancel()
                }
            }}
            title="Rubro"
            size="xl"
            tipName={TIPS.NAME.MODALS.RUBRO_MODAL}
            body={(
                <>
                    <Accordion title="Datos del rubro" open={datosOpen} onToggle={setDatosOpen}>
                        <div className="row form-basic">
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="codigo" required>Código</FormLabel>
                                <input
                                    type="text"
                                    name="codigo"
                                    maxLength={50}
                                    className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                                    value={formValues.codigo}
                                    onChange={formHandle}
                                    disabled={disabled}
                                />
                                <FormError message={errorOf('codigo')} />
                            </div>
                            <div className="col-12 col-md-8">
                                <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                                <input
                                    type="text"
                                    name="nombre"
                                    className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                                    value={formValues.nombre}
                                    onChange={formHandle}
                                    disabled={disabled}
                                />
                                <FormError message={errorOf('nombre')} />
                            </div>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="orden" required>Orden</FormLabel>
                                <input
                                  name="orden"
                                  type="number"
                                  placeholder=""
                                  className={`form-control ${errorOf('orden') ? 'is-invalid' : ''}`}
                                  value={formValues.orden}
                                  onChange={formHandle}
                                  disabled={disabled}
                                />
                                <FormError message={errorOf('orden')} />
                            </div>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="orden" required>Agrupamiento</FormLabel>
                                <input
                                  name="agrupamiento"
                                  type="text"
                                  placeholder=""
                                  className={`form-control ${errorOf('agrupamiento') ? 'is-invalid' : ''}`}
                                  value={formValues.agrupamiento}
                                  onChange={formHandle}
                                  disabled={disabled}
                                />
                                <FormError message={errorOf('agrupamiento')} />
                            </div>
                            <div className="mb-3 col-12 col-md-4">
                                <label htmlFor="fechaBaja" className="form-label">Fecha baja</label>
                                <DatePickerCustom
                                    name="fechaBaja"
                                    placeholder=""
                                    className="form-control"
                                    value={formValues.fechaBaja}
                                    onChange={formHandle}
                                    disabled={disabled}
                                />
                                <FormError />
                            </div>
                            <div className="col-12 col-md-3">
                                <FormLabel htmlFor="orden" required>Categoría</FormLabel>
                                <input
                                  name="categoria"
                                  type="text"
                                  placeholder=""
                                  className={`form-control ${errorOf('categoria') ? 'is-invalid' : ''}`}
                                  value={formValues.categoria}
                                  onChange={formHandle}
                                  disabled={disabled}
                                />
                                <FormError message={errorOf('categoria')} />
                            </div>
                            <div className="col-12 col-md-3">
                                <label htmlFor="orden" className="form-label">Importe mínimo</label>
                                <input
                                  name="importeMinimo"
                                  type="number"
                                  placeholder=""
                                  className="form-control"
                                  value={formValues.importeMinimo}
                                  onChange={formHandle}
                                  disabled={disabled}
                                />
                                <FormError />
                            </div>
                            <div className="mb-3 col-12 col-md-4">
                                <FormLabel htmlFor="esEmbarcacion" required>Tipo mínimo aplicable</FormLabel>
                                <InputLista
                                    name="idTipoMinimoAplicable"
                                    placeholder=""
                                    className={`form-control ${errorOf('idTipoMinimoAplicable') ? 'is-invalid' : ''}`}
                                    lista="TipoMinimoAplicable"
                                    value={formValues.idTipoMinimoAplicable}
                                    onChange={formHandle}
                                    disabled={disabled}
                                />
                                <FormError message={errorOf('idTipoMinimoAplicable')} />
                            </div>
                            <div className="mb-3 col-12 col-md-5">
                                <CheckboxButtonGroup label="Opciones">
                                    <CheckboxButton
                                        name="facturable"
                                        label="Facturable"
                                        checked={formValues.facturable}
                                        onChange={formHandle}
                                        disabled={disabled}
                                    />
                                    <CheckboxButton
                                        name="generico"
                                        label="Genérico"
                                        checked={formValues.generico}
                                        onChange={formHandle}
                                        disabled={disabled}
                                    />
                                    <CheckboxButton
                                        name="regimenGeneral"
                                        label="Régimen general"
                                        checked={formValues.regimenGeneral}
                                        onChange={formHandle}
                                        disabled={disabled}
                                    />
                                </CheckboxButtonGroup>
                            </div>
                        </div>
                    </Accordion>
                    {internalMode !== 'add' && (
                        <Accordion title="Alícuotas" startOpen={true}>
                            <RubroAlicuotasGrid
                                alicuotas={alicuotas}
                                disabled={disabled}
                                onChange={setAlicuotas}
                            />
                        </Accordion>
                    )}
                </>
            )}
            footer={(
                <div className="width-100p disp-b">
                    <button className="btn back-button m-left-5" data-dismiss="modal" onClick={() => {
                        if (internalMode !== props.editMode) {
                            props.onConfirm()
                        } else {
                            props.onCancel()
                        }
                    }}>Volver</button>
                    {internalMode !== 'view' && (
                        <button className="btn action-button" data-dismiss="modal" onClick={() => onConfirm()}>Guardar</button>
                    )}
                </div>
            )}
        />
    )
}

export default RubroModal
